import React, { forwardRef } from 'react';
import { Button, CircularProgress, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { GRADIENTS } from '../../theme/tokens';

/**
 * AuthPrimaryButton
 * Orange-gradient CTA button built on MUI Button + framer-motion.
 * Loading: text → spinner + background shimmer (shimmer skipped under reduced-motion).
 * Press: whileTap scale 0.97 (skipped under reduced-motion via MotionConfig at app root).
 *
 * @param {boolean} [loading=false]
 * @param {React.ReactNode} children
 * @param {object} [sx]
 * @param {string} [type='button']
 * @param {boolean} [disabled]
 */
const AuthPrimaryButton = forwardRef(function AuthPrimaryButton(
  { loading = false, children, sx = {}, disabled, type = 'button', onClick, ...rest },
  ref,
) {
  const theme = useTheme();
  const reduced = useReducedMotionPreferred();

  const gradient = GRADIENTS.brandDeep; // #C94E10 → #F26522 → #F7B32B

  // Shimmer: animates background-position only — no transform, no filter.
  // Skipped when loading is false or reduced-motion is active.
  const shimmerStyles =
    loading && !reduced
      ? {
          backgroundSize: '200% 100%',
          backgroundPosition: '0% 0%',
          animation: 'authBtnShimmer 1.4s ease-in-out infinite',
          '@keyframes authBtnShimmer': {
            '0%': { backgroundPosition: '0% 0%' },
            '50%': { backgroundPosition: '100% 0%' },
            '100%': { backgroundPosition: '0% 0%' },
          },
        }
      : {};

  return (
    <motion.div
      whileTap={reduced ? {} : { scale: 0.97 }}
      style={{ width: '100%' }}
    >
      <Button
        ref={ref}
        type={type}
        fullWidth
        variant="contained"
        size="large"
        disabled={disabled || loading}
        onClick={onClick}
        sx={{
          py: 1.5,
          borderRadius: theme.radius?.pill ?? 999,
          background: gradient,
          fontWeight: 700,
          fontSize: '1rem',
          textTransform: 'none',
          letterSpacing: 0.3,
          color: '#FFFFFF',
          boxShadow: theme.shadowsExt?.glowPrimary ?? '0 8px 28px rgba(242,101,34,0.28)',
          '&:hover': {
            background: gradient,
            boxShadow: '0 12px 32px rgba(242,101,34,0.4)',
            opacity: 0.92,
          },
          '&.Mui-disabled': {
            background: gradient,
            opacity: 0.48,
            color: '#FFFFFF',
          },
          ...shimmerStyles,
          ...sx,
        }}
        {...rest}
      >
        {loading ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          children
        )}
      </Button>
    </motion.div>
  );
});

export default AuthPrimaryButton;
